import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Plus, Shield, ShieldOff, ShieldAlert, Zap, StopCircle, Pencil } from 'lucide-react';
import { eventTypes, generateRuleSummary } from '../../../data/rulesData';
import type { Rule, RuleStatus } from '../../../types/rule.types';

interface RulesListProps {
  rules: Rule[];
  onEditRule?: (rule: Rule) => void;
  onAddRule?: () => void;
  isLoading?: boolean;
}

const statusConfig: Record<RuleStatus, { label: string; icon: typeof Shield; className: string }> = {
  active: { label: 'Active', icon: Shield, className: 'bg-active/10 text-active border-active/20' },
  draft: { label: 'Draft', icon: ShieldAlert, className: 'bg-draft/10 text-draft border-draft/20' },
  inactive: { label: 'Inactive', icon: ShieldOff, className: 'bg-muted text-muted-foreground border-border' },
};

function RuleCard({ rule, onEdit }: { rule: Rule; onEdit?: (rule: Rule) => void }) {
  const status = statusConfig[rule.status];
  const StatusIcon = status.icon;
  const eventLabels = rule.eventTypes.map(et => eventTypes.find(e => e.id === et)?.label ?? et);

  return (
    <div
      role="button"
      tabIndex={0}
      className="group bg-studio-surface rounded-lg border border-border hover:border-foreground/20 p-4 transition-colors cursor-pointer"
      onClick={() => onEdit?.(rule)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit?.(rule); } }}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-[11px] font-semibold text-muted-foreground">
          {rule.priority}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground truncate">{rule.name}</span>
            <Badge variant="outline" className={`text-[10px] h-5 shrink-0 ${status.className}`}>
              <StatusIcon className="h-2.5 w-2.5 mr-1" />
              {status.label}
            </Badge>
            {rule.stopAfterMatch && (
              <Badge variant="outline" className="text-[10px] h-5 shrink-0 border-border text-muted-foreground">
                <StopCircle className="h-2.5 w-2.5 mr-1" />
                Stop after match
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {eventLabels.map(label => (
              <Badge key={label} variant="secondary" className="text-[10px] h-5 font-normal">
                <Zap className="h-2.5 w-2.5 mr-1 text-email" />
                {label}
              </Badge>
            ))}
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed">
            {generateRuleSummary(rule)}
          </p>

          <p className="text-[11px] text-muted-foreground/60">
            v{rule.version} · Updated by {rule.updatedBy} · {rule.conditionGroup.conditions.length + rule.conditionGroup.groups.reduce((sum, g) => sum + g.conditions.length, 0)} conditions
          </p>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => { e.stopPropagation(); onEdit?.(rule); }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function RulesList({ rules, onEditRule, onAddRule, isLoading }: RulesListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-xs text-muted-foreground">Loading rules...</span>
      </div>
    );
  }

  if (rules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Shield className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-foreground">No rules configured</p>
          <p className="text-xs text-muted-foreground">Create a rule to define when this template should be used.</p>
        </div>
        <Button size="sm" className="h-8 text-xs" onClick={onAddRule}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Create Rule
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-tour="rules-list">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{rules.length} rule{rules.length !== 1 ? 's' : ''}</p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAddRule} data-tour="rules-add-btn">
          <Plus className="h-3 w-3 mr-1" />
          Add Rule
        </Button>
      </div>

      <div className="space-y-2">
        {rules.map(rule => (
          <RuleCard key={rule.ruleId} rule={rule} onEdit={onEditRule} />
        ))}
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-dashed border-border p-3 bg-muted/30" data-tour="rules-fallback">
        <ShieldOff className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">If no rule matches: <span className="font-medium text-foreground">Use default template</span></span>
      </div>
    </div>
  );
}
